import {
  formatLegacyScheduleHoursLabel,
  shiftNetWorkHours,
} from "../../utils/scheduleDurationUtils";
import {
  addAppCalendarDays,
  formatAppIntlLabel,
  formatAppTime,
  getAppDisplayTimezone,
  getAppTodayIso,
  parseDbDateTime,
  toAppDateKey,
} from "../../utils/appDateTime";

export function assignmentCountsTowardScheduleFilled(status) {
  const s = String(status ?? "").trim().toLowerCase();
  return (
    s === "confirmed" ||
    s === "completed" ||
    s === "in_progress" ||
    s === "active"
  );
}

export function shiftDayKey(startDate) {
  if (!startDate) return "";
  const key = toAppDateKey(startDate);
  if (key) return key;
  return String(startDate).slice(0, 10);
}

export function formatDayParts(isoDate) {
  const anchor = parseDbDateTime(`${isoDate}T12:00:00Z`);
  if (!anchor) {
    return { day: isoDate, date: "" };
  }
  const tz = getAppDisplayTimezone();
  return {
    day: formatAppIntlLabel(anchor, { weekday: "long", timeZone: tz }),
    date: formatAppIntlLabel(anchor, { month: "long", day: "numeric", timeZone: tz }),
  };
}

export function formatDayHeader(isoDate) {
  const { day, date } = formatDayParts(isoDate);
  return date ? `${day} ${date}` : day;
}

export function formatTimeRange(start, end) {
  const a = formatAppTime(start, { emptyValue: "" });
  const b = formatAppTime(end, { emptyValue: "" });
  if (!a && !b) return "";
  return `${a} - ${b}`;
}

function countGroupConfirmed(shifts) {
  let confirmed = 0;
  let required = 0;
  let activeShiftsCount = 0;
  for (const shift of shifts) {
    const assignments = shift.assignments || [];
    const isCancelled =
      String(shift.shift_state || "").toLowerCase() === "cancelled" ||
      String(shift.shift_state || "").toLowerCase() === "declined";
    const isUnassigned = assignments.length === 0;
    if (isCancelled || isUnassigned) {
      continue;
    }
    activeShiftsCount += 1;
    required += Math.max(1, Number(shift.required_staff) || assignments.length || 1);
    for (const a of assignments) {
      if (assignmentCountsTowardScheduleFilled(a.state)) confirmed += 1;
    }
  }
  return { confirmed, required: Math.max(required, activeShiftsCount) };
}

export function buildClientScheduleDays(shifts, dateFrom, dateRangeDays) {
  const range = Number(dateRangeDays) > 0 ? Number(dateRangeDays) : 7;
  const startKey = dateFrom || getAppTodayIso();
  const dayKeys = [];
  for (let i = 0; i < range; i += 1) {
    dayKeys.push(addAppCalendarDays(startKey, i));
  }

  const byDay = new Map();
  for (const key of dayKeys) {
    byDay.set(key, []);
  }

  for (const shift of shifts || []) {
    const key = shiftDayKey(shift.start_date);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(shift);
  }

  return dayKeys.map((isoDate) => {
      const dayShifts = byDay.get(isoDate) || [];
      const { day, date } = formatDayParts(isoDate);
      const groupsMap = new Map();

      for (const shift of dayShifts) {
        const groupKey = `${shift.client_id}-${shift.client_location_id}`;
        if (!groupsMap.has(groupKey)) {
          groupsMap.set(groupKey, {
            clientId: shift.client_id,
            clientName: shift.client_name,
            locationId: shift.client_location_id,
            locationName: shift.location_name,
            sublocation: shift.location_name,
            shifts: [],
          });
        }
        groupsMap.get(groupKey).shifts.push(shift);
      }

      const groups = Array.from(groupsMap.values()).map((group) => {
        const totalHours = group.shifts.reduce(
          (sum, s) => sum + shiftNetWorkHours(s),
          0,
        );
        const { confirmed, required } = countGroupConfirmed(group.shifts);
        return {
          ...group,
          totalHours,
          confirmedCount: confirmed,
          requiredStaff: required,
        };
      });

      const dayHours = groups.reduce((sum, g) => sum + g.totalHours, 0);
      const dayConfirmed = groups.reduce((sum, g) => sum + g.confirmedCount, 0);
      const dayRequired = groups.reduce((sum, g) => sum + g.requiredStaff, 0);

      return {
        isoDate,
        day,
        date,
        totalHours: dayHours,
        confirmedCount: dayConfirmed,
        requiredStaff: dayRequired,
        confirmedShifts: `${dayConfirmed}/${dayRequired}`,
        groups,
      };
    });
}

export function buildAllGroupKeys(days) {
  const keys = {};
  (days || []).forEach((day, dayIdx) => {
    (day.groups || []).forEach((_, groupIdx) => {
      keys[`${dayIdx}-${groupIdx}`] = true;
    });
  });
  return keys;
}

export function scheduleGroupMatchesSearch(group, searchTrimmed) {
  if (!searchTrimmed) return true;
  const q = searchTrimmed.toLowerCase();
  const hay = [
    group.clientName,
    group.locationName,
    group.sublocation,
    ...(group.shifts || []).flatMap((s) => [
      s.position_name,
      String(s.shift_id),
      ...(s.assignments || []).map((a) => a.name),
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export { formatLegacyScheduleHoursLabel };
